import { Grid, TextField, Typography } from '@mui/material';
import { CuriosityAutocomplete, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LanguageAutocomplete } from '@/legacy/components/LanguageAutocomplete/LanguageAutocomplete';
import { I18nNamespace } from '~/common/types';
import { CurrencySelect } from '~/components/CurrencySelect/CurrencySelect';
import { useFormFieldErrorTranslation } from '~/hooks';

export const UserProfileForm = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'UserProfileDetailsWidget' });
  const { errorToHelperText } = useFormFieldErrorTranslation();
  return (
    <Grid
      container
      direction="row"
      spacing={4}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        alignContent: 'flex-start',
      }}
    >
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('workingHours')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="workingHours"
          defaultValue={0}
          render={({ field, fieldState: { error } }) => (
            <TextField
              type="number"
              fullWidth={true}
              slotProps={{
                htmlInput: { min: 0 },
              }}
              {...field}
              error={!!error}
              helperText={errorToHelperText(error)}
              onChange={(e) => {
                // need to parse string to number specifically for zod form resolver
                field.onChange(parseInt(e.target.value));
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('commodityGroupIds')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="commodityGroupIds"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.COMMODITY_GROUP}
              multiple
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('capabilityIds')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="capabilityIds"
          defaultValue={[]}
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.CAPABILITY}
              multiple
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('organizationId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="organizationId"
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.ORGANIZATION}
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('deliveryLocationId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="deliveryLocationId"
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.LOCATION}
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('accountingAreaId')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="accountingAreaId"
          render={({ field, fieldState: { error } }) => (
            <CuriosityAutocomplete
              ns={CuriosityNamespaceType.ACCOUNTING_AREA}
              fullWidth={true}
              simplifyValue
              {...field}
              TextFieldProps={{
                error: !!error,
                helperText: errorToHelperText(error),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('currency')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="currency"
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <CurrencySelect
              {...field}
              error={!!error}
              helperText={errorToHelperText(error)}
              disabled={field.disabled}
              required
              fullWidth
            />
          )}
        />
      </Grid>
      <Grid
        container
        size={{ xs: 12, sm: 4 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('language')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Controller
          name="language"
          render={({ field, fieldState: { error } }) => (
            <LanguageAutocomplete
              {...field}
              value={field?.value || null}
              onChange={(_, items) => {
                field.onChange(items.toUpperCase());
              }}
              TextFieldProps={{
                error: !!error,
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
